import React from 'react';
import {
  ChatCircle, ChatCircleDots, House, ArrowLeft, PaperPlaneTilt, X
} from '@phosphor-icons/react';

export const ContractorChatSidebar = ({
  chatOpen, setChatOpen, chatView, setChatView,
  chatPartnerId, setChatPartnerId, chatPartnerName,
  conversations, chatMessages, chatInput, setChatInput,
  chatSending, chatScrollRef, currentUserId,
  openChatWith, handleSendChat, fetchConversations
}) => {
  if (!chatOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end" data-testid="contractor-chat-sidebar">
        <div className="absolute inset-0 bg-black/40" onClick={() => setChatOpen(false)} />
        <div className="relative w-full max-w-sm bg-white dark:bg-[#1A1A1A] border-l-4 border-black shadow-[-8px_0_20px_rgba(0,0,0,0.3)] flex flex-col h-full" style={{ animation: 'slideIn 0.25s ease-out' }}>
          {/* Header */}
          <div className="flex items-center justify-between p-3 bg-[#2563EB] border-b-2 border-black flex-shrink-0">
            <div className="flex items-center gap-2">
              {chatView === 'chat' && (
                <button onClick={() => { setChatView('list'); setChatPartnerId(null); fetchConversations(); }} className="text-white/80 hover:text-white" data-testid="chat-back-to-list">
                  <ArrowLeft size={20} weight="bold" />
                </button>
              )}
              <ChatCircleDots size={20} weight="bold" className="text-white" />
              <h3 className="text-white font-black uppercase text-sm tracking-wider">
                {chatView === 'chat' ? chatPartnerName : 'Messages'}
              </h3>
            </div>
            <button onClick={() => setChatOpen(false)} className="text-white/80 hover:text-white" data-testid="contractor-chat-close">
              <X size={20} weight="bold" />
            </button>
          </div>

          {/* Conversations List View */}
          {chatView === 'list' && (
            <div className="flex-1 overflow-y-auto" data-testid="contractor-conversations-list">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <ChatCircle size={40} weight="bold" className="text-neutral-300 dark:text-neutral-600 mb-2" />
                  <p className="text-sm text-neutral-500 font-bold">No conversations yet</p>
                  <p className="text-xs text-neutral-400 mt-1">Messages from homeowners will appear here</p>
                </div>
              ) : conversations.map((conv) => {
                const p = conv.partner;
                const lastMsg = conv.last_message;
                const unread = conv.unread_count || 0;
                return (
                  <button
                    key={p._id}
                    onClick={() => openChatWith(p._id, p.name)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-left border-b border-neutral-200 dark:border-neutral-700"
                    data-testid={`contractor-conv-${p._id}`}
                  >
                    <div className="w-10 h-10 bg-[#3B82F6] border-2 border-black flex items-center justify-center overflow-hidden flex-shrink-0">
                      {p.profile_image ? (
                        <img src={p.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <House size={18} weight="bold" className="text-white" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-neutral-900 dark:text-white text-sm truncate">{p.name}</p>
                        {lastMsg && (
                          <span className="text-[10px] text-neutral-400 flex-shrink-0 ml-2">
                            {new Date(lastMsg.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                      {lastMsg && (
                        <p className="text-xs text-neutral-500 truncate mt-0.5">
                          {lastMsg.sender_id === currentUserId ? 'You: ' : ''}{lastMsg.content}
                        </p>
                      )}
                    </div>
                    {unread > 0 && (
                      <span className="w-5 h-5 bg-[#3B82F6] text-[#0F172A] text-[10px] font-bold flex items-center justify-center flex-shrink-0 border border-black">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Individual Chat View */}
          {chatView === 'chat' && chatPartnerId && (
            <>
              <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-neutral-100 dark:bg-neutral-900/80">
                {chatMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <ChatCircle size={40} weight="bold" className="text-neutral-300 dark:text-neutral-600 mb-2" />
                    <p className="text-sm text-neutral-500 font-bold">Start a conversation</p>
                    <p className="text-xs text-neutral-400 mt-1">Messages are private between you and {chatPartnerName}</p>
                  </div>
                ) : chatMessages.map((msg) => {
                  const isMine = msg.sender_id === currentUserId;
                  return (
                    <div key={msg._id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 text-sm ${isMine ? 'bg-[#2563EB] text-[#0F172A] border border-[#203A55]' : 'bg-white dark:bg-[#0F172A] text-neutral-900 dark:text-white border border-neutral-300 dark:border-neutral-700'}`}>
                        <p className="break-words leading-relaxed">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-neutral-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={handleSendChat} className="flex items-center gap-2 p-3 bg-white dark:bg-[#0F172A] border-t-2 border-black flex-shrink-0">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:border-[#2B4C6F]"
                  data-testid="contractor-chat-input"
                />
                <button type="submit" disabled={!chatInput.trim() || chatSending} className="h-10 w-10 flex items-center justify-center bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] border-2 border-black shadow-[2px_2px_0px_#000] disabled:opacity-40 flex-shrink-0 transition-all" data-testid="contractor-chat-send">
                  <PaperPlaneTilt size={18} weight="bold" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
};
